// Passkey auth for admin.onlytrade.ai
// All calls hit /auth/* which nginx proxies to the shared auth-service on :3020.
// The auth-service sets HTTP-only cookies (kc_access, kc_refresh) — scoped to .onlytrade.ai.
// We ALSO mirror the access token in localStorage because the existing /api/* admin backend
// (kommercio-admin on :3003) expects Authorization: Bearer <jwt>. The auth-service's
// access token carries the user's role in the payload, so it's compatible enough for the
// current admin backend auth middleware that verifies JWT with a shared secret.

import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';

const TOKEN_KEY = 'ot_access_token';

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setAccessToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function authFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const resp = await fetch(`/auth${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try { msg = (await resp.json()).error || msg; } catch {}
    throw new Error(msg);
  }
  if (resp.status === 204) return undefined as T;
  return resp.json() as Promise<T>;
}

export const supportsWebAuthn = browserSupportsWebAuthn;

export type SessionUser = {
  id: string;
  email: string;
  role: string;
  display_name: string | null;
  tenant_id: string | null;
};

// ─── Magic link flow (bootstrap / new device) ──────────────────────────────
export async function sendMagicLink(email: string): Promise<void> {
  await authFetch<{ ok: true }>('/magic/send', {
    method: 'POST',
    body: JSON.stringify({ email, surface: 'admin.onlytrade.ai' }),
  });
}

export async function verifyMagicLink(token: string): Promise<{ bootstrapToken: string; email: string }> {
  return authFetch<{ bootstrapToken: string; email: string; surface: string }>('/magic/verify', {
    method: 'POST',
    body: JSON.stringify({ token, surface: 'admin.onlytrade.ai' }),
  });
}

// ─── Passkey registration (after magic link bootstrap) ────────────────────
export async function registerPasskey(bootstrapToken: string, deviceName: string): Promise<SessionUser> {
  const { options } = await authFetch<{ options: Parameters<typeof startRegistration>[0]['optionsJSON'] }>('/passkeys/register-start', {
    method: 'POST',
    body: JSON.stringify({ bootstrapToken, deviceName }),
  });
  const attestation = await startRegistration({ optionsJSON: options });
  const r = await authFetch<{ user: SessionUser; access: string }>('/passkeys/register-finish', {
    method: 'POST',
    body: JSON.stringify({ bootstrapToken, attestation, deviceName }),
  });
  setAccessToken(r.access);
  return r.user;
}

// ─── Passkey login ─────────────────────────────────────────────────────────
export async function loginWithPasskey(email?: string): Promise<SessionUser> {
  const { options } = await authFetch<{ options: Parameters<typeof startAuthentication>[0]['optionsJSON'] }>('/passkeys/login-start', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  const assertion = await startAuthentication({ optionsJSON: options });
  const r = await authFetch<{ user: SessionUser; access: string }>('/passkeys/login-finish', {
    method: 'POST',
    body: JSON.stringify({ email, assertion }),
  });
  setAccessToken(r.access);
  return r.user;
}

// ─── Password login (fallback for when passkey isn't available) ──────────
export async function loginWithPassword(email: string, password: string): Promise<SessionUser> {
  const r = await authFetch<{ user: SessionUser; access: string }>('/password/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setAccessToken(r.access);
  return r.user;
}

export async function setPassword(new_password: string, current_password?: string): Promise<void> {
  await authFetch('/password/set', {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAccessToken() || ''}` },
    body: JSON.stringify({ new_password, current_password }),
  });
}

// ─── Session management ────────────────────────────────────────────────────
export async function fetchMe(): Promise<SessionUser | null> {
  try {
    const r = await authFetch<{ user: SessionUser }>('/sessions/me');
    return r.user;
  } catch { return null; }
}

export async function refreshSession(): Promise<{ user: SessionUser; access: string } | null> {
  try {
    const r = await authFetch<{ user: SessionUser; access: string }>('/sessions/refresh', { method: 'POST' });
    setAccessToken(r.access);
    return r;
  } catch { return null; }
}

export async function logout(): Promise<void> {
  try { await authFetch('/sessions/logout', { method: 'POST' }); } catch {}
  setAccessToken(null);
}

// ─── Admin user management ────────────────────────────────────────────────
export type AuthUser = {
  id: string;
  email: string;
  display_name: string | null;
  role: string;
  tenant_id: string | null;
  surfaces: string[];
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  credential_count: number;
};

export const authAdmin = {
  listUsers: (params: { role?: string; search?: string; surface?: string; page?: number; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, String(v)); });
    return authFetch<{ data: AuthUser[]; total: number; totalPages: number; page: number }>(`/admin/users?${qs}`);
  },
  invite: (body: { email: string; role: string; surface: string; display_name?: string }) =>
    authFetch<{ user: AuthUser }>('/admin/users/invite', { method: 'POST', body: JSON.stringify(body) }),
  sendMagicLink: (userId: string, surface: string) =>
    authFetch<{ ok: true }>(`/admin/users/${userId}/magic-link`, { method: 'POST', body: JSON.stringify({ surface }) }),
  setStatus: (userId: string, is_active: boolean) =>
    authFetch<{ ok: true }>(`/admin/users/${userId}/status`, { method: 'PATCH', body: JSON.stringify({ is_active }) }),
  update: (userId: string, body: Partial<{ role: string; surfaces: string[]; display_name: string }>) =>
    authFetch<{ ok: true }>(`/admin/users/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  listCredentials: (userId: string) =>
    authFetch<{ data: Array<{ id: string; credential_id: string; device_name: string; rp_id: string; last_used_at: string | null; created_at: string }> }>(`/admin/users/${userId}/credentials`),
  revokeCredential: (credId: string) =>
    authFetch<{ ok: true }>(`/admin/credentials/${credId}`, { method: 'DELETE' }),
  auditLog: (params: { user_id?: string; event?: string; page?: number; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, String(v)); });
    return authFetch<{ data: Array<{ id: number; user_id: string | null; event: string; surface: string | null; ip: string | null; metadata: Record<string, unknown>; created_at: string }> }>(`/admin/audit?${qs}`);
  },
};
